import { useApiClient } from './apiClient';
import { ApiRoutes } from '@/constants/apiConstants';
import type { UserResponse, UserRequest, UserProfileResponse, UpdateUserProfileRequest, ChangePasswordRequest } from '@/types/user';
import { UserListItem } from '@/types/user';
import { useAuthenticationStore } from '@/stores/authenticationStore';

const PROFILE_STORAGE_KEY = 'ruumi_user_profile';

class UserService {
  async getUsers(): Promise<UserListItem[]> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<UserResponse[]>(
      ApiRoutes.User.GetUsers
    )) as unknown as UserResponse[];

    return response.map((item) => new UserListItem(item));
  }

  async getAvailableUsersForEmployee(employeeId?: number): Promise<UserListItem[]> {
    const { apiClient } = await useApiClient();
    const params = employeeId ? { employeeId } : {};
    const response = (await apiClient.get<UserResponse[]>(
      ApiRoutes.User.GetAvailableUsersForEmployee,
      { params }
    )) as unknown as UserResponse[];

    return response.map((item) => new UserListItem(item));
  }

  async getUserById(userId: number): Promise<UserListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.get<UserResponse>(
      `${ApiRoutes.User.GetUser}?userId=${userId}`
    )) as unknown as UserResponse;

    return new UserListItem(response);
  }

  async createUser(request: UserRequest): Promise<UserListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<UserResponse>(
      ApiRoutes.User.CreateUser,
      request
    )) as unknown as UserResponse;

    return new UserListItem(response);
  }

  async updateUser(request: UserRequest): Promise<UserListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<UserResponse>(
      ApiRoutes.User.UpdateUser,
      request
    )) as unknown as UserResponse;

    return new UserListItem(response);
  }

  async unlinkUserRelation(userId: number): Promise<UserListItem> {
    const { apiClient } = await useApiClient();
    const response = (await apiClient.post<UserResponse>(
      `${ApiRoutes.User.UnlinkUserRelation}?userId=${userId}`
    )) as unknown as UserResponse;

    return new UserListItem(response);
  }

  async deleteUser(userId: number): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.post<void>(`${ApiRoutes.User.DeleteUser}?userId=${userId}`);
  }

  async initiatePasswordReset(userId: number): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.post<void>(`${ApiRoutes.User.InitiatePasswordReset}?userId=${userId}`);
  }

  async getMyProfile(): Promise<UserProfileResponse> {
    const authStore = useAuthenticationStore();
    const currentUser = authStore.currentUser;

    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    // Try to load stored profile for this user from localStorage
    try {
      const stored = localStorage.getItem(PROFILE_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as UserProfileResponse;
        if (parsed.userId === currentUser.userId) {
          return parsed;
        }
      }
    } catch (err) {
      console.warn('Failed to parse stored user profile:', err);
    }

    const fullName = currentUser.fullName || '';
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    const now = new Date().toISOString();

    const profile: UserProfileResponse = {
      userProfileId: 0,
      userId: currentUser.userId,
      firstName,
      lastName,
      email: '',
      phone: null,
      preferredLanguageLookupId: null,
      preferredLanguageName: null,
      employeeId: null,
      clientId: null,
      fullName: fullName || currentUser.username,
      userName: currentUser.username,
      roleName: currentUser.roleName,
      createdOn: now,
      modifiedOn: null
    };

    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch {
      // Ignore storage errors
    }

    return profile;
  }

  async updateMyProfile(request: UpdateUserProfileRequest): Promise<UserProfileResponse> {
    const authStore = useAuthenticationStore();
    const currentUser = authStore.currentUser;

    if (!currentUser) {
      throw new Error('User is not authenticated');
    }

    const existing = await this.getMyProfile();
    const now = new Date().toISOString();

    const updated: UserProfileResponse = {
      ...existing,
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phone: request.phone ?? null,
      preferredLanguageLookupId: request.preferredLanguageLookupId ?? null,
      fullName: `${request.firstName} ${request.lastName}`.trim() || existing.fullName,
      modifiedOn: now
    };

    try {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Ignore storage errors
    }

    return updated;
  }

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    const { apiClient } = await useApiClient();
    await apiClient.post<void>(ApiRoutes.User.ChangePassword, request);
  }
}

export default new UserService();
